/**
 * leadRouterDealer.ts — typed client + pure helpers for the Lead Router v2
 * DEALER LAYER (Squad S8, AI-egoj, 2026-07-22). Backs M6 (Dealer Management, web)
 * and the dealer-primary / auto-routing controls.
 *
 * Endpoints (mounted at /api/v1/admin/lead-router/...):
 *   GET    /dealers                    — list (M6), ?managerId=
 *   POST   /dealers                    — create (422 on duplicate product group)
 *   PATCH  /dealers/:id                — update
 *   PUT    /manager-config/:managerId  — autoDealerRouting toggle
 *   POST   /dealer-primary             — mark (dealer,group,area) dealer-primary
 *   DELETE /dealer-primary             — unmark (rep-primary again)
 */
import { api } from '@/utils/api';

// ─── Types ──────────────────────────────────────────────────────────────────

export type ServedAreaType = 'pincode' | 'city' | 'district' | 'state';
export interface ServedArea {
  type: ServedAreaType;
  value?: string;
  name?: string; // legacy shape
  pincodes?: string[]; // bulk pincode entry
}

export interface DealerPrimaryRow {
  id: string;
  dealerId: string;
  productGroupId: string | null;
  matchType: 'pincode' | 'city' | 'district' | 'state' | 'catchall';
  matchValue: string;
}

export interface RouterDealerV2 {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  gstin: string | null;
  servedAreas: ServedArea[];
  productGroups: string[];
  managerUserId: string | null;
  waGroupId: string | null;
  followUpOwnerId: string | null;
  isActive: boolean;
  isDemo: boolean;
  dealerPrimary: DealerPrimaryRow[];
  autoDealerRouting: boolean;
}

export interface DealerCreateBody {
  name: string;
  productGroups?: string[];
  servedAreas?: ServedArea[];
  phone?: string | null;
  email?: string | null;
  gstin?: string | null;
  managerUserId?: string | null;
  city?: string | null;
  state?: string | null;
}

export interface DealerPrimaryBody {
  dealerId: string;
  productGroupKey?: string | null;
  productGroupId?: string | null;
  matchType: 'pincode' | 'city' | 'district' | 'state' | 'catchall';
  matchValue: string;
}

// ─── Pure helpers (unit-tested; no network) ───────────────────────────────────

/** Parse a free-text pincode field (comma/space/newline separated) → digit
 *  strings, order-preserving + deduped. Ignores non-6-... just keeps digits. */
export function parsePincodeInput(raw: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const tok of String(raw ?? '').split(/[\s,;\n]+/)) {
    const d = tok.replace(/\D/g, '');
    if (d && !seen.has(d)) { seen.add(d); out.push(d); }
  }
  return out;
}

/** First product group listed twice (case-insensitive), else null. Mirrors the
 *  backend hard-lock so the UI can block before the 422. */
export function duplicateProductGroup(groups: string[]): string | null {
  const seen = new Set<string>();
  for (const g of groups) {
    const k = (g ?? '').trim().toLowerCase();
    if (!k) continue;
    if (seen.has(k)) return g;
    seen.add(k);
  }
  return null;
}

/** All pincodes a dealer declares (reads legacy `name`, new `value`, bulk `pincodes`). */
export function dealerPincodes(servedAreas: ServedArea[] | undefined): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const add = (v: unknown) => {
    const d = String(v ?? '').replace(/\D/g, '');
    if (d && !seen.has(d)) { seen.add(d); out.push(d); }
  };
  for (const a of servedAreas ?? []) {
    if (a?.type !== 'pincode') continue;
    if (Array.isArray(a.pincodes)) a.pincodes.forEach(add);
    if (a.value != null) add(a.value);
    if (a.name != null) add(a.name);
  }
  return out;
}

/** Turn a list of pincodes into normalized servedAreas entries. */
export function pincodesToServedAreas(pincodes: string[]): ServedArea[] {
  return pincodes.map((p) => ({ type: 'pincode' as const, value: p }));
}

/** Is this (group, area) currently dealer-primary for the dealer? */
export function isDealerPrimary(
  rows: DealerPrimaryRow[],
  productGroupId: string | null,
  matchType: string,
  matchValue: string,
): boolean {
  const mv = matchType === 'pincode' ? matchValue.replace(/\D/g, '') : matchValue.trim().toLowerCase();
  return rows.some(
    (r) => r.productGroupId === productGroupId && r.matchType === matchType && r.matchValue === mv,
  );
}

/** M6 badge for the rep-vs-dealer tie-break state. */
export function primaryLabel(dealerPrimary: boolean): string {
  return dealerPrimary ? 'Dealer-primary ✓' : 'Rep-wins (default)';
}

/** M6 badge for the hand-off automation state. */
export function autoLabel(auto: boolean): string {
  return auto ? 'AUTO — no human gate' : 'Semi-auto';
}

// ─── API ─────────────────────────────────────────────────────────────────────

export const leadRouterDealerApi = {
  list(managerId?: string): Promise<RouterDealerV2[]> {
    return api
      .get<{ success: true; data: RouterDealerV2[] }>('/admin/lead-router/dealers', {
        params: managerId ? { managerId } : undefined,
      })
      .then((r) => r.data.data);
  },

  create(body: DealerCreateBody): Promise<RouterDealerV2> {
    return api
      .post<{ success: true; data: RouterDealerV2 }>('/admin/lead-router/dealers', body)
      .then((r) => r.data.data);
  },

  patch(id: string, body: Partial<DealerCreateBody & { isActive: boolean }>): Promise<RouterDealerV2> {
    return api
      .patch<{ success: true; data: RouterDealerV2 }>(`/admin/lead-router/dealers/${id}`, body)
      .then((r) => r.data.data);
  },

  setAutoRouting(managerId: string, autoDealerRouting: boolean): Promise<void> {
    return api
      .put(`/admin/lead-router/manager-config/${managerId}`, { autoDealerRouting })
      .then(() => undefined);
  },

  markDealerPrimary(body: DealerPrimaryBody): Promise<void> {
    return api.post('/admin/lead-router/dealer-primary', body).then(() => undefined);
  },

  unmarkDealerPrimary(body: DealerPrimaryBody): Promise<void> {
    return api.delete('/admin/lead-router/dealer-primary', { data: body }).then(() => undefined);
  },
};
